import React, { useState, useEffect, useRef } from "react";
import {
  BottomHorizDivider,
  CollapsibleTabWrapper,
  CollapsibleTextBox,
  CollapsibleWrapper,
  ShowHideTab,
  LeftVerticalDivider,
  TopHorizDivider,
} from "./styles";
import { ShowHideIcon } from "./renderIcon";

const DEFAULT_PRIMARY_COLOR = "rgb(33, 150, 243)";

const FORMATS = {
  horizontal: "horizontal",
  vertical: "vertical",
  none: "none",
};

interface CollapsibleContentProps {
  attributes: any;
  children: React.ReactNode;
  collapsibleData: {
    showTitle?: string;
    hideTitle?: string;
    isIconUsed?: boolean;
    iconPos?: string;
    iconStyle?: string;
    format?: string;
  };
  readOnly?: boolean;
}

function CollapsibleContent(props: CollapsibleContentProps) {
  const {
    collapsibleData: { showTitle, hideTitle, isIconUsed, iconPos, iconStyle, format },
    readOnly = true,
  } = props;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [theme, setTheme] = useState({ primaryColor: DEFAULT_PRIMARY_COLOR });
  const [animDuration] = useState("0.2s");
  const collapsibleElem = useRef<HTMLDivElement>(null);
  const barPadding = 8;
  const boxMargin = 10;

  // In read-only mode, we can use a simple theme
  useEffect(() => {
    // For viewer, we use default theme
    setTheme({ primaryColor: DEFAULT_PRIMARY_COLOR });
  }, []);

  useEffect(() => {
    if (isCollapsed && collapsibleElem.current) {
      collapsibleElem.current.style.height = "0px";
    }
  }, [isCollapsed]);

  function handleTabToggled() {
    if (isTransitioning) return;
    if (!isCollapsed) {
      collapseSection();
    } else {
      expandSection();
    }
  }

  function handleOnKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleTabToggled();
      event.stopPropagation();
    }
  }

  function collapseSection() {
    if (!collapsibleElem.current) return;
    setIsTransitioning(true);
    const sectionHeight = collapsibleElem.current.scrollHeight - barPadding - boxMargin * 2;
    collapsibleElem.current.style.height = `${sectionHeight}px`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (collapsibleElem.current) {
          collapsibleElem.current.style.height = "0px";
        }
      });
    });

    const afterAnim = () => {
      if (collapsibleElem.current) {
        collapsibleElem.current.removeEventListener("transitionend", afterAnim);
        collapsibleElem.current.style.display = "none";
        setIsTransitioning(false);
      }
    };
    collapsibleElem.current.addEventListener("transitionend", afterAnim);
    setIsCollapsed(true);
  }

  function expandSection() {
    if (!collapsibleElem.current) return;
    setIsTransitioning(true);
    collapsibleElem.current.style.display = "initial";
    const sectionHeight = collapsibleElem.current.scrollHeight - barPadding + boxMargin * 2;
    collapsibleElem.current.style.height = `${sectionHeight}px`;

    const afterAnim = () => {
      if (collapsibleElem.current) {
        collapsibleElem.current.removeEventListener("transitionend", afterAnim);
        collapsibleElem.current.style.height = "auto";
        setIsTransitioning(false);
      }
    };
    collapsibleElem.current.addEventListener("transitionend", afterAnim);
    setIsCollapsed(false);
  }

  const themeProps = {
    primaryColor: theme.primaryColor,
  };
  const label = isCollapsed ? showTitle || "Show" : hideTitle || "Hide";
  return (
    <CollapsibleWrapper
      data-slate-void
      isVerticalFormat={format === FORMATS.vertical}
      {...props.attributes}
    >
      {format === FORMATS.vertical && <LeftVerticalDivider theme={themeProps} />}
      <CollapsibleTabWrapper theme={themeProps} isVerticalFormat={format === FORMATS.vertical}>
        <ShowHideTab
          readOnly={readOnly}
          theme={themeProps}
          onClick={handleTabToggled}
          onKeyDown={handleOnKeyDown}
          isCollapsed={isCollapsed}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? showTitle || "Show" : hideTitle || "Hide"}
        >
          {isIconUsed && (
            <ShowHideIcon iconStyle={iconStyle} iconPos={iconPos} isCollapsed={isCollapsed} />
          )}
          {label}
        </ShowHideTab>
      </CollapsibleTabWrapper>
      <TopHorizDivider barPadding={barPadding} theme={themeProps} aria-hidden="true">
        {format === FORMATS.horizontal && <div className={"bar-line"} />}
      </TopHorizDivider>
      <CollapsibleTextBox
        animDuration={animDuration}
        boxMargin={boxMargin}
        ref={collapsibleElem}
        theme={themeProps}
        readOnly={readOnly}
        isCollapsed={isCollapsed}
        aria-hidden={isCollapsed}
        isVerticalFormat={format === FORMATS.vertical}
      >
        {props.children}
      </CollapsibleTextBox>
      <BottomHorizDivider
        animDuration={animDuration}
        barPadding={barPadding}
        theme={themeProps}
        isCollapsed={isCollapsed}
        aria-hidden="true"
        isVerticalFormat={format === FORMATS.vertical}
      >
        {format === FORMATS.horizontal && <div className={"bar-line"} />}
      </BottomHorizDivider>
    </CollapsibleWrapper>
  );
}

export default CollapsibleContent;
